/**
 * ai-custom.js — Bloque "Generar con IA" (HTML/CSS/JS libre).
 *
 * A diferencia del resto de bloques (props estructuradas → HTML), este renderiza
 * código ARBITRARIO generado por la IA dentro de un <iframe sandbox> AISLADO:
 *   sandbox="allow-scripts"  (SIN allow-same-origin → origin nulo)
 * El código del bloque NO puede tocar el DOM de la página, cookies, sesión,
 * almacenamiento ni hacer red (salvo cargar las librerías CDN declaradas).
 * Es el modelo CodePen/JSFiddle: máxima libertad creativa, cero riesgo de XSS
 * sobre el sitio del cliente.
 *
 * Solo se inserta MANUALMENTE desde el editor (botón "Generar con IA"). La IA del
 * generador de sitios no puede producirlo (su tipo no existe en los schemas).
 *
 * Estructura de props:
 *   { html, css, js, lib_urls:{js:[],css:[]}, bgColor, iframeHeight, ai_desc, title }   (ai_desc: antes `_ai_desc`, que el saneador de guardado descartaba)
 */
import { store } from '../core/store.js';
import { cssTokenSafe } from '../utils/contrast.js';
import { getRenderLang, t } from '../utils/i18n.js';

// Hosts CDN permitidos dentro de la CSP del iframe.
// SINCRONIZAR con llm_schemas.CDN_HOSTS (el backend resuelve las URLs de librerías;
// aquí solo declaramos qué hosts puede cargar el sandbox).
const CDN_HOSTS = 'https://cdn.jsdelivr.net https://cdnjs.cloudflare.com';

const DEFAULT_HEIGHT = 480;
/** Por debajo de esto un alto no es un bloque: es un informe de 0 px (iframe aún sin pintar,
 *  escena que mide su propio iframe) que el puente antes redondeaba a 80 y GUARDABA. */
const MIN_HEIGHT = 120;

/** Tope de `devicePixelRatio` DENTRO del iframe. Un canvas a pantalla completa con DPR 2-3
 *  mueve 4-9 veces los píxeles de uno a DPR 1 y es lo que ahoga la página del cliente; a 1,5 la
 *  nitidez apenas cambia. */
const DPR_MAX = 1.5;

/** Idioma del documento del iframe: el de la página que se pinta, si tiene forma de etiqueta BCP 47
 *  sencilla (`es`, `en`, `pt-br`…); si no, español. Va a un atributo: nada que no case entra. */
const LANG_OK = /^[a-z]{2,3}(-[a-z0-9]{2,8})?$/i;

function langDelBloque() {
    const lang = getRenderLang();
    return (typeof lang === 'string' && LANG_OK.test(lang)) ? lang : 'es';
}

/**
 * PRELUDIO del documento del iframe: corre en el `<head>`, ANTES de las librerías y del JS del
 * usuario (que lo escribe el modelo y no controlamos). Dos redes, cada una en su try/catch:
 *   1. DPR ≤ 1,5 — sobrescribe el getter de `devicePixelRatio` (propio de `window` en Chrome, del
 *      prototipo en otros): el JS que dimensiona su canvas con él pinta menos píxeles.
 *   2. `roundRect` SOLO si falta (Safari < 16, Firefox < 112): el modelo lo usa a menudo y, sin
 *      él, la escena entera muere en su try/catch. Radio uniforme (número; lista → el primero;
 *      `{x}` → x), recortado a la mitad del lado menor.
 * Probado en Chrome 153 dentro del sandbox con DPR 2 forzado → 1.5.
 */
const PRELUDIO = `
(function(){
  try {
    var d = Object.getOwnPropertyDescriptor(window, 'devicePixelRatio') ||
            Object.getOwnPropertyDescriptor(Window.prototype, 'devicePixelRatio');
    if (d && d.get) {
      var g = d.get;
      Object.defineProperty(window, 'devicePixelRatio', { configurable: true, get: function () { return Math.min(g.call(window) || 1, ${DPR_MAX}); } });
    }
  } catch (e) {}
  try {
    var rr = function (x, y, w, h, r) {
      if (Array.isArray(r)) r = r[0];
      if (r && typeof r === 'object') r = r.x;
      r = Math.min(Math.abs(Number(r) || 0), Math.abs(w) / 2, Math.abs(h) / 2);
      var sw = w < 0 ? -1 : 1;
      this.moveTo(x + sw * r, y);
      this.arcTo(x + w, y, x + w, y + h, r);
      this.arcTo(x + w, y + h, x, y + h, r);
      this.arcTo(x, y + h, x, y, r);
      this.arcTo(x, y, x + w, y, r);
      this.closePath();
    };
    [window.CanvasRenderingContext2D, window.Path2D].forEach(function (C) {
      if (C && C.prototype && typeof C.prototype.roundRect !== 'function') C.prototype.roundRect = rr;
    });
  } catch (e) {}
})();`;

/**
 * ALTURA del bloque: el alto que el iframe informa al contenedor (`nube3i-ai-block:height`) es el
 * del CONTENIDO, que sí encoge. Va en su propio <script> del `<head>`, tras el PRELUDIO: antes de
 * las librerías, del HTML y del JS del usuario, y en su propio ámbito (un `var P`, `medir` o
 * `informar` del código generado no lo puede pisar).
 *
 * POR QUÉ NO `documentElement.scrollHeight` (lo que se medía hasta el 28-09-2026): dentro de un
 * iframe nunca baja del alto del propio iframe (su viewport), así que el iframe podía crecer pero
 * NUNCA encoger. Visto en producción: una escena `height: clamp(340px, 60vw, 780px)` guardada a
 * 780 en el escritorio se quedaba en el MÓVIL con el iframe a 780 y 435 px de fondo vacío debajo
 * de sus 345 px. `medir()` toma el borde inferior más bajo de los hijos del <body> (sin los
 * `display:none` ni los `position:fixed`; los `display:contents` se miran por dentro) + su
 * `margin-bottom` + el `padding-bottom`/borde del body, redondeado hacia arriba. Con
 * `offsetTop + offsetHeight` —la caja de LAYOUT, sin transformaciones— y no con
 * getBoundingClientRect: el `translateY`/`scale` de una entrada animada del contenedor raíz no
 * cambia su tamaño (ningún ResizeObserver avisa al acabar), así que medirlo dejaría pegado un alto
 * de más o, con un `scale(.9)` de partida, uno de MENOS que recortaría el bloque. Con el body
 * estático, el `offsetTop` de sus hijos va en coordenadas del documento (Blink/WebKit; con el reset
 * `html,body{margin:0;padding:0}` de abajo, la regla de la especificación da lo mismo); con el body
 * posicionado es relativo a su caja y se le suma su borde superior. Un SVG suelto no tiene
 * `offset*` y va por su rectángulo. Sin hijos medibles no se informa (el iframe se queda como está).
 *
 * Los bloques YA guardados —y el código que genere la IA con la costumbre vieja— mandan su propio
 * `parent.postMessage({type:'nube3i-ai-block:height', height: document.documentElement.scrollHeight})`
 * en sus resize(). Por eso, antes de que corra nada suyo, `window.parent` (atributo [Replaceable]
 * en WebIDL: asignarlo crea una propiedad propia) pasa a ser un Proxy del padre real cuyo
 * `postMessage` cambia esos mensajes por la medida de aquí y reenvía todo lo demás tal cual; el
 * resto de propiedades (`length`, `top`, `closed`…) son las del padre real, errores incluidos.
 *
 * Cuándo se informa: ResizeObserver sobre <html>, <body> y cada hijo del body (con
 * `html,body{height:100%}` el <html> no cambia de tamaño aunque el contenido crezca: el acordeón
 * que se abre lo avisa su hijo), `load`, `resize` y los setTimeout de siempre (60/80/400/1200/2500
 * ms desde DOMContentLoaded, que llega justo después del JS del usuario). Un contenido circular
 * (`height:100vh`, `min-height:100vh` —con el `box-sizing:border-box` de abajo, su padding va
 * dentro—, un absoluto con `inset:0` contra el viewport) mide exactamente el alto actual del
 * iframe: se queda quieto, sin bucle y sin hundirse, aunque tampoco encoge (el prompt pide alturas
 * derivadas del ancho o del contenido). Lo que sume algo FUERA de una caja de 100vh (un
 * `margin-bottom`) sigue creciendo hasta el tope de 20000 px del puente, igual que antes.
 */
const ALTURA = `
(function(){
  var P = window.parent;
  if (!P || P === window) return;
  var Y = function () { return window.pageYOffset || 0; };
  function medir() {
    var b = document.body;
    if (!b) return 0;
    var bs = getComputedStyle(b);
    var base = bs.position !== 'static' ? b.getBoundingClientRect().top + Y() : 0;
    var fondo = -1;
    (function recorrer(lista) {
      for (var i = 0; i < lista.length; i++) {
        var el = lista[i], cs = getComputedStyle(el);
        if (cs.display === 'none' || cs.position === 'fixed') continue;
        if (cs.display === 'contents') { recorrer(el.children); continue; }
        var abajo = typeof el.offsetHeight === 'number'
          ? base + el.offsetTop + el.offsetHeight
          : el.getBoundingClientRect().bottom + Y();
        abajo += parseFloat(cs.marginBottom) || 0;
        if (abajo > fondo) fondo = abajo;
      }
    })(b.children);
    if (fondo < 0) return 0;
    return Math.ceil(fondo + (parseFloat(bs.paddingBottom) || 0) + (parseFloat(bs.borderBottomWidth) || 0));
  }
  function informar() {
    var h = medir();
    if (!h) return;
    try { P.postMessage({ type: 'nube3i-ai-block:height', height: h }, '*'); } catch (e) {}
  }
  try {
    var pm = function (msg) {
      if (msg && typeof msg === 'object' && msg.type === 'nube3i-ai-block:height') { informar(); return; }
      return P.postMessage.apply(P, arguments);
    };
    var intermediario = new Proxy(P, { get: function (t, k) { return k === 'postMessage' ? pm : Reflect.get(t, k); } });
    window.parent = intermediario;
    if (window.parent !== intermediario) {
      Object.defineProperty(window, 'parent', { value: intermediario, configurable: true, writable: true });
    }
  } catch (e) {}
  function arrancar() {
    try {
      var ro = new ResizeObserver(informar);
      ro.observe(document.documentElement);
      ro.observe(document.body);
      for (var i = 0; i < document.body.children.length; i++) ro.observe(document.body.children[i]);
    } catch (e) {}
    [60, 80, 400, 1200, 2500].forEach(function (t) { setTimeout(informar, t); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arrancar);
  else arrancar();
  window.addEventListener('load', informar);
  window.addEventListener('resize', informar);
})();`;

/** Escapa una cadena para incrustarla como valor del atributo srcdoc="...". */
function escAttr(str) {
    return String(str == null ? '' : str)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;');
}

/** Construye el documento HTML completo que vive DENTRO del iframe sandbox. */
function buildSrcdoc(p) {
    const libUrls = p.lib_urls || { js: [], css: [] };
    const cssLinks = (libUrls.css || []).map(u => `<link rel="stylesheet" href="${u}">`).join('');
    const libScripts = (libUrls.js || []).map(u => `<script src="${u}"><\/script>`).join('');

    // CSP estricta: sin red (connect-src 'none') salvo cargar scripts/estilos de los
    // hosts CDN permitidos. Permite inline para el css/js del bloque.
    const csp = [
        "default-src 'none'",
        `script-src 'unsafe-inline' ${CDN_HOSTS}`,
        `style-src 'unsafe-inline' ${CDN_HOSTS}`,
        "img-src https: data: blob:",
        "font-src https: data:",
        "media-src https: data: blob:",
        "connect-src 'none'",
        "frame-src 'none'",
        "base-uri 'none'",
        "form-action 'none'",
    ].join('; ');

    const userCss = p.css || '';
    const userHtml = p.html || '';
    // Neutralizar un "</script>" dentro del JS (cerraría el <script> inline del
    // iframe). En JS válido solo aparece dentro de strings/regex, donde "<\/script>"
    // es equivalente. Impacto acotado al iframe aislado, pero evita romper el bloque.
    const userJs = (p.js || '').replace(/<\/script>/gi, '<\\/script>');

    // Bootstrap: envuelve el JS del usuario en try/catch. El alto del iframe lo informa ALTURA
    // (su propio <script> del <head>, antes de todo lo del usuario).
    const bootstrap = `
(function(){
  try {
${userJs}
  } catch(err){ console.error('[ai-block]', err); }
})();`;

    return `<!doctype html><html lang="${langDelBloque()}"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta http-equiv="Content-Security-Policy" content="${csp}">
<script>${PRELUDIO}<\/script>
<script>${ALTURA}<\/script>
${cssLinks}
<style>
*,*::before,*::after{box-sizing:border-box}
html,body{margin:0;padding:0}
body{font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif}
img,canvas,svg,video{max-width:100%}
${userCss}
</style>
</head><body>
${userHtml}
${libScripts}
<script>${bootstrap}<\/script>
</body></html>`;
}

/**
 * Renderer del bloque. Recibe (props, index). Devuelve el HTML de la sección.
 */
export const aiCustomBlock = (rawProps, index) => {
    const p = rawProps || {};
    // XSS (PDR-S1 / C-1): este bloque es el ÚNICO que NO pasa por `safe()` —sus props son
    // html/css/js, no copy— y pinta `bgColor` DOS veces en el `style` del <section>. Sin este
    // filtro se queda fuera del cinturón que cubre a los otros 31 bloques.
    const bg = (p.bgColor && cssTokenSafe(p.bgColor)) ? p.bgColor : '#0f172a';
    // Un alto guardado por debajo de MIN_HEIGHT es un bloque ATASCADO, no una elección: el puente
    // de abajo convertía un informe de 0 px en 80 y lo persistía (visto en producción: una escena a
    // pantalla completa reducida a una franja de 80 px con las nubes). Se pinta con el alto por
    // defecto y el puente vuelve a medir desde ahí: los bloques ya rotos se curan solos al abrir.
    const guardado = Number(p.iframeHeight);
    const height = guardado >= MIN_HEIGHT ? guardado : DEFAULT_HEIGHT;
    const isEmpty = !(p.html || p.css || p.js);

    if (isEmpty) {
        return `
  <section id="bloque-ai-custom-${index}" class="ai-custom-section ai-custom-empty" style="--bg:${bg}; background:${bg}; min-height:220px; display:flex; align-items:center; justify-content:center; padding:60px 8%;">
    <div style="text-align:center; font-family:system-ui,sans-serif; color:#94a3b8;">
      <div style="font-size:1rem; font-weight:700;">Bloque IA vacío</div>
      <div style="font-size:0.85rem; margin-top:6px; opacity:0.8;">Usa &ldquo;Regenerar&rdquo; para crear contenido.</div>
    </div>
  </section>`;
    }

    const srcdoc = escAttr(buildSrcdoc(p));
    // sandbox SIN allow-same-origin: el documento corre con origin nulo, aislado del sitio.
    return `
  <section id="bloque-ai-custom-${index}" class="ai-custom-section" style="--bg:${bg}; background:${bg}; padding:0; position:relative;">
    <iframe class="ai-custom-frame" data-block-index="${index}" title="${escAttr(t('ai_block_title'))}"
            sandbox="allow-scripts"
            loading="lazy" scrolling="no"
            style="width:100%; height:${height}px; border:0; display:block; overflow:hidden;"
            srcdoc="${srcdoc}"></iframe>
  </section>`;
};

// ───────────────────────────────────────────────────────────────
//  PUENTE DE ALTURA (instalado una sola vez por runtime — editor y público).
//  El iframe (origin nulo) emite postMessage con el alto de su CONTENIDO (ALTURA,
//  arriba); aquí ajustamos el alto real del <iframe> y, en el editor, persistimos en
//  props.iframeHeight para que sobreviva al re-render y al guardado/compilado.
// ───────────────────────────────────────────────────────────────
(function installAiBlockBridge() {
    if (typeof window === 'undefined' || window.__aiBlockBridgeInstalled) return;
    window.__aiBlockBridgeInstalled = true;

    // Durante un arrastre del editor los iframes se vuelven inertes (`ai-block-dragging`) para que
    // dragover/drop lleguen a los wrappers: eso lo gobierna `editor/dragdrop.js`, que es quien sabe
    // cuándo ACABA un arrastre aunque su `dragend` no llegue (vivía aquí y se quedaba pegado con él).

    window.addEventListener('message', (e) => {
        const d = e.data;
        if (!d || d.type !== 'nube3i-ai-block:height') return;
        // El `if (!h) return` iba DESPUÉS de un `Math.max(80, …)`, así que un 0 nunca era 0: se
        // volvía 80 y se persistía. Ahora todo informe por debajo de MIN_HEIGHT se ignora: un
        // documento que mide menos que eso no ha pintado todavía o se mide a sí mismo (100vh
        // dentro del iframe es el propio iframe), y en ninguno de los dos casos es un alto real.
        const h = Math.min(20000, parseInt(d.height, 10) || 0);
        if (h < MIN_HEIGHT) return;

        const frames = document.querySelectorAll('iframe.ai-custom-frame');
        for (const f of frames) {
            if (f.contentWindow === e.source) {
                f.style.height = h + 'px';
                // NO se persiste con la «Vista Móvil» del editor: ahora el alto informado SÍ baja (ver
                // ALTURA arriba) y guardar ahí hornearía el alto del teléfono en `iframeHeight`, que es
                // el alto con el que NACE el iframe publicado: el escritorio arrancaría encogido y
                // saltaría al medirse (28-09-2026). Tampoco con un drawer abierto (el Estudio de la
                // sección, fase 4 del rediseño): la página le cede 376 px, el bloque se mide a ese ancho
                // y, además de hornearlo, el cambio entraba en el historial como un paso fantasma.
                if (store.get('editorMode') && !document.body.classList.contains('mobile-view')
                    && !document.documentElement.classList.contains('n3i-drawer-open')) {
                    const idx = parseInt(f.getAttribute('data-block-index'), 10);
                    const blocks = store.get('siteBlocks');
                    if (Array.isArray(blocks) && blocks[idx]
                        && blocks[idx].type === 'ai-custom' && blocks[idx].props) {
                        blocks[idx].props.iframeHeight = h;
                    }
                }
                break;
            }
        }
    });
})();
